import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DarkSidebar } from "./DarkSidebar";
import { Menu } from "lucide-react";
import { HeaderCreditsDisplay } from "@/components/credits/HeaderCreditsDisplay";

export const DarkNavigation = ({ 
  activeView, 
  setActiveView,
  showRoles,
  setShowRoles 
}: { 
  activeView: string; 
  setActiveView: (view: string) => void;
  showRoles: boolean;
  setShowRoles: (show: boolean) => void;
}) => {
  const { toast } = useToast();

  const handleViewChange = (view: string) => {
    setActiveView(view);
    localStorage.setItem('lastActiveView', view);
    
    toast({
      title: `Switched to ${view === 'chat' ? 'Stewie Chat' : 'Stewie Trader'}`,
      description: `You are now using ${view === 'chat' ? 'Stewie Chat' : 'Stewie Trader'}.`,
      variant: "info",
    });
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      const { error } = await supabase.auth.signOut();
      window.location.href = '/auth';
      
      if (error && !error.message?.includes('session_not_found')) {
        toast({
          title: "Warning",
          description: "There was an issue during logout, but you have been signed out successfully.",
          variant: "warning",
        });
        console.error('Logout error:', error);
      } else {
        toast({
          title: "Logged out successfully",
          description: "You have been logged out of your account.",
          variant: "success",
        });
      }
    } catch (error: any) {
      console.error('Logout error:', error);
      window.location.href = '/auth';
      toast({
        title: "Warning",
        description: "There was an issue during logout, but you have been signed out successfully.",
        variant: "warning",
      });
    }
  };

  return (
    <nav className="bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50 shadow-lg w-full">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Sheet>
                <SheetTrigger asChild>
                  <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <Menu className="h-5 w-5 md:hidden text-slate-300" />
                    <div className="relative h-10 w-10">
                      <img
                        src="/lovable-uploads/e3b5de11-4d35-4574-a2a6-3b0df6dc385d.png"
                        alt="Stewie Trader"
                        className="h-full w-full object-contain rounded-full ring-2 ring-slate-600/50"
                      />
                    </div>
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 bg-transparent border-none">
                  <div className="h-full w-64 bg-slate-900/95 backdrop-blur-sm shadow-lg flex flex-col overflow-y-auto border-r border-slate-700/50">
                    <div className="flex flex-col space-y-3 p-4 border-b border-slate-700/50">
                      <div className="flex items-center justify-around">
                        <button
                          onClick={() => handleViewChange('chat')}
                          className={
                            `${activeView === 'chat' ? 'text-blue-400 font-semibold' : 'text-slate-300 hover:text-slate-100'} text-sm transition-colors`
                          }
                        >
                          Stewie Chat
                        </button>
                        <button
                          onClick={() => handleViewChange('trader')}
                          className={
                            `${activeView === 'trader' ? 'text-blue-400 font-semibold' : 'text-slate-300 hover:text-slate-100'} text-sm transition-colors`
                          }
                        >
                          Stewie Trader
                        </button>
                      </div>
                      
                      {/* Mobile Credits Display */}
                      <div className="flex justify-center pt-2">
                        <HeaderCreditsDisplay />
                      </div>
                    </div>
                    <DarkSidebar />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <button
                onClick={() => handleViewChange('chat')}
                className={`${
                  activeView === 'chat'
                    ? 'border-blue-400 text-slate-100'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
              >
                Stewie Chat
              </button>
              <button
                onClick={() => handleViewChange('trader')}
                className={`${
                  activeView === 'trader'
                    ? 'border-blue-400 text-slate-100'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors`}
              >
                Stewie Trader
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Credits Display */}
            <HeaderCreditsDisplay />
            
            <Button
              variant="ghost"
              onClick={() => {
                setShowRoles(!showRoles);
                if (!showRoles) {
                  toast({
                    title: "AI Roles Panel Opened",
                    description: "Select an AI role to assist you with specific tasks.",
                    variant: "info",
                  });
                }
              }}
              className="rounded-[4px] text-slate-300 hover:text-slate-100 hover:bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
            >
              AI Roles
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-[4px] border-slate-700/50 text-slate-300 hover:text-slate-100 hover:bg-slate-800/50 hover:border-slate-600"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}; 